import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, tap, catchError, finalize, of } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { DataStoreResponseModel } from '../../../core/models/registers/response/StoreResponseModel';
import { EconomicGroupModel, DataEconomicGroupModel } from '../../../core/models/licensed/economic-group.model';
import { UsersService } from '../../../core/services/users.service';
import { LicensedService } from '../../../core/services/licensed.service';
import { DataEconomicGroupResponse } from '../../../core/models/licensed/economic-group-response.model';
import { CnpjListResponseModel } from '../../../core/models/licensed/cnpj-list-response.model';
import { EconomicGroupRequest } from '../../../core/models/users/create-user-request.model';

@Component({
  selector: 'app-create-user-modal',
  templateUrl: './create-user-modal.component.html',
  styleUrl: './create-user-modal.component.scss'
})
export class CreateUserModalComponent implements OnInit, OnDestroy {
  @Input() show: boolean = false;
  @Input() set preSelectedStore(value: DataStoreResponseModel | null) {
    this._preSelectedStore = value;
    this.preSelectStoreData();
  }
  get preSelectedStore(): DataStoreResponseModel | null {
    return this._preSelectedStore;
  }
  private _preSelectedStore: DataStoreResponseModel | null = null;
  
  @Output() closeModal = new EventEmitter<void>();
  @Output() userCreated = new EventEmitter<any>();

  private destroy$ = new Subject<void>();
  
  formCreateUser!: FormGroup;
  isLoadingCreateUser: boolean = false;
  successCreateUser: boolean = false;
  
  allEconomicGroup: EconomicGroupModel = {} as EconomicGroupModel;
  filteredEconomicGroup: DataEconomicGroupModel[] = [];
  gruposEconomicos: any[] = [];
  isCreateEconomicGroupListVisible: boolean = false;
  
  allCNPJ: any = {};
  filteredCNPJ: any[] = [];
  groupCNPJs: any[] = []; 
  cnpjList: any[] = [];
  selectedCNPJs: string[] = [];
  isCreateCNPJListVisible: boolean = false;
  
  emailConflictExists: boolean = false;
  emailErrorMessage: string = '';
  selectionError: boolean = false;

  get isPreSelectedMode(): boolean {
    return !!this.preSelectedStore;
  }

  constructor(
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    private licensedService: LicensedService,
    private cdr: ChangeDetectorRef
  ) {
    this.buildFormCreateUser();
  }

  ngOnInit(): void {
    this.loadEconomicGroups();
    this.loadCNPJs();
    
    if (this.preSelectedStore) {
      this.preSelectStoreData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildFormCreateUser(): void {
    this.formCreateUser = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      permission: [''],
      economic_group_name: this.formBuilder.array([]),
      included_cnpj: this.formBuilder.array([]),
      economicGroup: [''],
      cnpj: ['']
    });

    this.formCreateUser.get('email')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.emailConflictExists) {
          this.emailConflictExists = false;
          this.emailErrorMessage = '';
        }
      });
  }

  private preSelectStoreData(): void {
    console.log('preSelectStoreData chamado. Store:', this.preSelectedStore);
    if (this.preSelectedStore && this.preSelectedStore.cnpj) {
      const cnpjAlreadyExists = this.cnpjList.some(cnpj => cnpj.cnpj === this.preSelectedStore!.cnpj);
      
      if (!cnpjAlreadyExists) {
        const storeCnpj = {
          cnpj: this.preSelectedStore.cnpj,
          company_name: this.preSelectedStore.company_name || this.preSelectedStore.trade_name || this.preSelectedStore.cnpj
        };
        
        this.cnpjList = [storeCnpj];
        this.selectionError = false;
        
        console.log('CNPJ pré-selecionado:', storeCnpj);
      }

      if (this.preSelectedStore.economic_group) {
        const groupAlreadyExists = this.gruposEconomicos.some(
          grupo => grupo.id === this.preSelectedStore!.economic_group!.id
        );
        
        if (!groupAlreadyExists) {
          const storeEconomicGroup = {
            id: this.preSelectedStore.economic_group.id,
            economic_group_name: this.preSelectedStore.economic_group.economic_group_name,
            status: this.preSelectedStore.economic_group.status
          };
          
          this.gruposEconomicos = [storeEconomicGroup];
          console.log('Grupo econômico pré-selecionado:', storeEconomicGroup);

          this.loadCNPJsFromEconomicGroup(storeEconomicGroup.id, storeEconomicGroup.economic_group_name);
        }
      }
    }
  }

  private loadEconomicGroups(): void {
    this.licensedService.getEconomicGroup(0)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.allEconomicGroup = response;
          this.filteredEconomicGroup = response.data || [];
        },
        error: (error) => {
          console.error('Erro ao carregar grupos econômicos:', error);
        }
      });
  }

  private loadCNPJs(): void {
    this.licensedService.getAllCNPJ()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.allCNPJ = response;
          this.filteredCNPJ = response.data || [];
        },
        error: (error) => {
          console.error('Erro ao carregar CNPJs:', error);
        }
      });
  }

  onSubmitCreateUser(): void {
    if (this.formCreateUser.invalid) {
      return;
    }

    if (this.gruposEconomicos.length === 0 && this.cnpjList.length === 0) {
      this.selectionError = true;
      return;
    }

    this.isLoadingCreateUser = true;
    this.selectionError = false;

    const createUserRequest = this.createUserRequest();

    this.usersService.createUser(createUserRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoadingCreateUser = false;
          this.successCreateUser = true;
          
          setTimeout(() => {
            this.userCreated.emit(response);
            this.onClose();
          }, 2000);
        },
        error: (error: HttpErrorResponse) => {
          this.isLoadingCreateUser = false;
          this.handleCreateUserError(error);
        }
      });
  }

  private createUserRequest(): any {
    const { email, permission } = this.formCreateUser.value;
    let economicGroups: any[] = [];
    let includedCnpjs: any[] = [];

    if (this.gruposEconomicos.length > 0) {
      economicGroups = this.gruposEconomicos.map(grupo => ({
        id: grupo.id,
        economic_group_name: grupo.economic_group_name
      }));
    }

    if (this.cnpjList.length > 0) {
      includedCnpjs = this.cnpjList.map(cnpj => ({
        cnpj: cnpj.cnpj
      }));
    }

    let request: any = {
      email,
      economic_group: economicGroups,
      included_cnpj: includedCnpjs
    };

    if (permission) {
      request.permission = permission;
    }

    return request;
  }

  onClose(): void {
    this.closeModal.emit();
    this.resetForm();
  }

  private resetForm(): void {
    this.formCreateUser.reset();
    this.isLoadingCreateUser = false;
    this.successCreateUser = false;
    this.emailConflictExists = false;
    this.selectionError = false;
    
    if (!this.preSelectedStore) {
      this.gruposEconomicos = [];
      this.cnpjList = [];
    } else {
      this.preSelectStoreData();
    }
  }

  private handleCreateUserError(error: HttpErrorResponse): void {
    console.error('Erro ao criar usuário:', error);
    
    if (error.status === 409) {
      this.emailConflictExists = true;
      
      const errorMessage = error.error?.message?.toLowerCase() || '';
      if (errorMessage.includes('user already exist') || errorMessage.includes('already exists') || errorMessage.includes('já existe')) {
        this.emailErrorMessage = 'Este e-mail já está sendo usado por outro usuário.';
      } else {
        this.emailErrorMessage = error.error?.message || 'E-mail já existe no sistema.';
      }
    } else if (error.status === 400) {
      this.emailConflictExists = true;
      this.emailErrorMessage = 'Dados inválidos. Verifique as informações fornecidas.';
    } else {
      this.emailConflictExists = true;
      this.emailErrorMessage = 'Erro ao criar usuário. Tente novamente.';
    }
  }

  addGrupoEconomico(item: DataEconomicGroupResponse): void {
    const exists = this.gruposEconomicos.find(grupo => grupo.id === item.id);
    if (exists) {
      return;
    }

    if (this.gruposEconomicos.length > 0) {
      this.gruposEconomicos = [];
      this.cnpjList = [];
    }

    this.gruposEconomicos.push({
      id: item.id,
      economic_group_name: item.economic_group_name
    });

    this.loadCNPJsFromEconomicGroup(item.id, item.economic_group_name);

    this.formCreateUser.get('economicGroup')?.setValue('');
    this.isCreateEconomicGroupListVisible = false;

    this.selectionError = false;
  }

  private loadCNPJsFromEconomicGroup(economicGroupId: number, groupName?: string): void {
    if (groupName && groupName.trim().toUpperCase() === 'FREEMIUM') {
      this.groupCNPJs = [];
      this.filteredCNPJ = [];
      return;
    }

    console.log('Iniciando busca de CNPJs para o grupo:', economicGroupId);
    this.licensedService.getCnpjListByEconomicGroupId(economicGroupId).subscribe({
      next: (response: CnpjListResponseModel) => {
        console.log('Resposta da busca de CNPJs:', response);
        if (response && response.data) {
          this.groupCNPJs = response.data;
          this.filteredCNPJ = [...this.groupCNPJs];
          
          const currentCnpjs = [...this.cnpjList];
          let addedCount = 0;
          
          response.data.forEach((cnpj: any) => {
            const existsInList = currentCnpjs.some(item => item.cnpj === cnpj.cnpj);
            if (!existsInList) {
              currentCnpjs.push(cnpj);
              addedCount++;
            }
          });
          
          if (addedCount > 0) {
            this.cnpjList = currentCnpjs;
            this.cdr.detectChanges();
            console.log('Lista de CNPJs atualizada com sucesso. Total:', this.cnpjList.length);
          } else {
            console.log('Nenhum CNPJ novo encontrado para adicionar.');
          }
        } else {
          console.warn('Resposta da API vazia ou sem dados.');
        }
      },
      error: (error: any) => {
        console.error('Erro ao buscar CNPJs do grupo econômico:', error);
      }
    });
  }

  removeGrupoEconomico(itemReq: EconomicGroupRequest): void {
    this.gruposEconomicos = this.gruposEconomicos.filter(item => item.id !== itemReq.id);

    this.removeCNPJsFromEconomicGroup(itemReq.id);
  }

  private removeCNPJsFromEconomicGroup(economicGroupId: number): void {
    this.licensedService.getCnpjListByEconomicGroupId(economicGroupId).subscribe({
      next: (response: CnpjListResponseModel) => {
        if (response && response.data) {
          response.data.forEach((cnpj: any) => {
            this.cnpjList = this.cnpjList.filter(item => item.cnpj !== cnpj.cnpj);
          });
        }
      },
      error: (error: any) => {
        console.error('Erro ao remover CNPJs do grupo econômico:', error);
      }
    });
  }

  onSearchCreateEconomicGroup(): void {
    const searchTerm = this.formCreateUser.get('economicGroup')?.value?.toLowerCase() || '';
    this.filteredEconomicGroup = this.allEconomicGroup.data?.filter((group: DataEconomicGroupModel) =>
      group.economic_group_name.toLowerCase().includes(searchTerm)
    ) || [];
  }

  addAllCNPJsFromStoreGroup(): void {
    if (this.preSelectedStore?.economic_group?.id) {
      this.loadCNPJsFromEconomicGroup(this.preSelectedStore.economic_group.id);
      this.isCreateCNPJListVisible = false;
    }
  }

  addCNPJ(cnpj: any): void {

    if (!this.cnpjList.find(c => c.cnpj === cnpj.cnpj)) {
      this.cnpjList.push(cnpj);
    }
    this.isCreateCNPJListVisible = false;
    this.formCreateUser.get('cnpj')?.setValue('');
  }

  removeCNPJ(cnpj: any): void {
    if (this.isPreSelectedMode && this.preSelectedStore?.cnpj === cnpj.cnpj) {
      return;
    }

    this.cnpjList = this.cnpjList.filter(c => c.cnpj !== cnpj.cnpj);
  }
  
   onSearchCreateCNPJ(): void {
    const searchTerm = this.formCreateUser.get('cnpj')?.value?.toLowerCase() || '';
    
    let sourceList: any[] = [];

    if (this.isPreSelectedMode && this.preSelectedStore?.economic_group) {
      sourceList = this.groupCNPJs;
    } else if (this.gruposEconomicos.length > 0) {
      sourceList = this.groupCNPJs;
    } else {
      sourceList = this.allCNPJ?.data || [];
    }

    if (!searchTerm) {
      this.filteredCNPJ = sourceList;
    } else {
      this.filteredCNPJ = sourceList.filter((cnpj: any) =>
        cnpj.cnpj?.toLowerCase().includes(searchTerm) ||
        cnpj.company_name?.toLowerCase().includes(searchTerm)
      );
    }
  }

  toggleCNPJ(cnpj: any): void {
    if (this.isPreSelectedMode && this.preSelectedStore?.cnpj === cnpj.cnpj) {
      return;
    }

    const index = this.cnpjList.findIndex(c => c.cnpj === cnpj.cnpj);
    if (index !== -1) {
      this.cnpjList.splice(index, 1);
    } else {
      this.cnpjList.push(cnpj);
    }
    this.formCreateUser.get('cnpj')?.setValue('');
  }

  isCNPJSelected(cnpj: any): boolean {
    return this.cnpjList.some(c => c.cnpj === cnpj.cnpj);
  }

  isAllGroupCNPJsSelected(): boolean {
    if (this.groupCNPJs.length === 0) return false;
    return this.groupCNPJs.every(groupCnpj => 
      this.cnpjList.some(selected => selected.cnpj === groupCnpj.cnpj)
    );
  }

  toggleAllGroupCNPJs(): void {
    if (this.groupCNPJs.length === 0) return;

    const allSelected = this.isAllGroupCNPJsSelected();
    
    if (allSelected) {
      this.groupCNPJs.forEach(groupCnpj => {
        if (this.isPreSelectedMode && this.preSelectedStore?.cnpj === groupCnpj.cnpj) {
          return; 
        }
        const index = this.cnpjList.findIndex(c => c.cnpj === groupCnpj.cnpj);
        if (index !== -1) {
          this.cnpjList.splice(index, 1);
        }
      });
    } else {
      this.groupCNPJs.forEach(groupCnpj => {
        if (!this.cnpjList.some(c => c.cnpj === groupCnpj.cnpj)) {
          this.cnpjList.push(groupCnpj);
        }
      });
    }
  }
}
